import {useEffect, useRef, useState} from 'react';

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

// Shared across hook instances so the script is only injected once
let scriptPromise;
const loadTurnstileScript = () => {
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      if (window.turnstile) {
        resolve(window.turnstile);
        return;
      }
      const script = document.createElement('script');
      script.src = TURNSTILE_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve(window.turnstile);
      script.onerror = (err) => {
        scriptPromise = undefined; // Allow a retry on a later mount
        reject(err);
      };
      document.head.appendChild(script);
    });
  }
  return scriptPromise;
};

/**
 * Renders a Cloudflare Turnstile captcha widget and exposes its token.
 *
 * Attach `turnstileRef` to a <div> inside the form, then submit
 * `turnstileToken` as the `cf-turnstile-response` field. The token is
 * an empty string until the challenge completes, and resets itself on
 * expiry (tokens are only valid for a few minutes).
 *
 * @param {Object} options
 * @param {string} options.siteKey Turnstile site key; the hook is inert without one
 * @param {boolean} [options.enabled] Set false to skip rendering entirely
 * @param {string} [options.appearance] Turnstile appearance mode; the default
 *   'interaction-only' stays invisible unless Cloudflare requires a challenge
 * @returns {{turnstileRef: Object, turnstileToken: string}}
 */
function useTurnstile({siteKey, enabled = true, appearance = 'interaction-only'}) {
  const turnstileRef = useRef(null);
  const [token, setToken] = useState('');
  const isActive = Boolean(enabled && siteKey);

  useEffect(() => {
    if (!isActive) return undefined;

    let widgetId;
    let cancelled = false;

    loadTurnstileScript()
      .then((turnstile) => {
        if (cancelled || !turnstileRef.current) return;
        widgetId = turnstile.render(turnstileRef.current, {
          sitekey: siteKey,
          appearance,
          // We surface the token ourselves rather than letting Turnstile
          // inject its own hidden input into the form
          'response-field': false,
          callback: (newToken) => setToken(newToken),
          'expired-callback': () => {
            setToken('');
            if (window.turnstile) window.turnstile.reset(widgetId);
          },
          'error-callback': () => setToken(''),
        });
      })
      .catch(() => {}); // Script blocked/unavailable; the server decides what to do

    return () => {
      cancelled = true;
      setToken('');
      if (widgetId !== undefined && window.turnstile) {
        window.turnstile.remove(widgetId);
      }
    };
  }, [isActive, siteKey, appearance]);

  return {turnstileRef, turnstileToken: token};
}

export default useTurnstile;
